"use strict";

import onInitModulesInsScope from "./onInitModulesInScope";
import { translate } from "@elements/translations";

/**
 * Lift-map ↔ lift-row highlight sync (Area 3 — content-map-lift-list).
 *
 * Modeled on hotspot-area.js's hover/click sync shape, but written fresh:
 * here the "markers" are fixed <g class="bahn" data-lift-id="..."> paths
 * inside an inlined SVG, not percentage-positioned buttons over an <img>.
 *
 * The lift-row list gets replaced wholesale by ajax-form on every date
 * change, the map does not — so bahn bindings only need to happen once
 * (on the initial full-page scope), while row bindings must be re-applied
 * on every swap. Both are handled the same way here since re-binding an
 * already-bound .bahn is harmless (jQuery just adds a no-op duplicate
 * scope query when .bahn isn't present in a swapped fragment).
 *
 * Since the two sides are only ever linked by matching data-lift-id, a
 * date's row list can (now or in the future, once real data replaces the
 * placeholder rows) omit a lift the static map still draws. updateBahnAvailability()
 * re-checks this on every swap and disables click/hover (and tab focus) on
 * any .bahn with no matching row, see .bahn--disabled.
 */

let activeLiftId = null;
let hoveredLiftId = null;

// The row list is swapped per date via ajax-form, so a lift drawn on the
// (static) map might not have a matching row for the currently active date.
function getMatchingRow(liftId) {
    return liftId ? document.querySelector('#liftRows .content-map-lift-list__row[data-lift-id="' + liftId + '"]') : null;
}

function hasMatchingRow(liftId) {
    return getMatchingRow(liftId) !== null;
}

// Keeps each .bahn's interactivity AND status coloring in sync with the
// current (date-swapped) row list. Per the Confluence spec: every Bahn is
// colored by its own status at all times — green (in Betrieb) or red (außer
// Betrieb), recomputed on every date change — not just the selected one.
// Only two colors exist on the map ("kein Gelb"); "Eingeschränkt" counts as
// green here (bahn--open) — its amber detail (badge/note) stays on the lift
// row only, never on the map. Disables click/hover (and removes it from tab
// order) for any lift that has no matching row right now, and drops stale
// active/hover state that would otherwise get stuck highlighted with no way
// to click it off again.
//
// The green/red coloring is the only place bahn status shows up, so it's
// also mirrored into aria-label here (recomputed from each bahn's original,
// name-only label — cached once in bindBahns — so repeat calls replace
// rather than append the status suffix).
function updateBahnAvailability() {
    if (activeLiftId && !hasMatchingRow(activeLiftId)) {
        activeLiftId = null;
    }
    if (hoveredLiftId && !hasMatchingRow(hoveredLiftId)) {
        hoveredLiftId = null;
    }

    $('.content-map-lift-list__map .bahn').each(function () {
        let $bahn = $(this);
        let liftId = $bahn.attr('data-lift-id');
        let row = getMatchingRow(liftId);
        let isAvailable = row !== null;
        let isClosed = isAvailable && row.getAttribute('data-state') === 'closed';
        let baseLabel = $bahn.data('baseLabel');

        $bahn
            .toggleClass('bahn--disabled', !isAvailable)
            .toggleClass('bahn--open', isAvailable && !isClosed)
            .toggleClass('bahn--closed', isClosed)
            .attr('tabindex', isAvailable ? '0' : '-1')
            .attr('aria-disabled', isAvailable ? null : 'true')
            .attr('aria-label', isAvailable
                ? baseLabel + ' – ' + translate(isClosed ? 'liftlist.state.closed' : 'liftlist.map.open')
                : baseLabel);
    });

    applyActiveHighlight();
}

function applyActiveHighlight() {
    let $bahns = $('.content-map-lift-list__map .bahn');
    let bahnIds = $bahns.toArray().map(function (b) { return $(b).attr('data-lift-id'); });
    // Active and hover are tracked independently on purpose: an active
    // (selected) bahn must keep its own highlighted color no matter what
    // else gets hovered — hovering a different bahn only ever adds a
    // highlight to that other bahn, it never takes the active one's away.
    let activeMatches = !!activeLiftId && bahnIds.indexOf(activeLiftId) !== -1;
    let hoverMatches = !!hoveredLiftId && bahnIds.indexOf(hoveredLiftId) !== -1;

    $bahns.each(function () {
        let id = $(this).attr('data-lift-id');
        let isActive = activeMatches && id === activeLiftId;
        let isHovered = hoverMatches && id === hoveredLiftId;
        $(this)
            .toggleClass('is-active', isActive || isHovered)
            .toggleClass('is-dim', (activeMatches || hoverMatches) && !isActive && !isHovered)
            .attr('aria-pressed', isActive ? 'true' : 'false');
    });

    // aria-pressed (not the hover classes) is the one bit of state that
    // needs to reach assistive tech — it's what turns "select a lift" from a
    // silent class toggle into an announced, perceivable result when a bahn
    // or row is activated by click or keyboard.
    $('#liftRows .content-map-lift-list__row').each(function () {
        let id = $(this).attr('data-lift-id');
        let isHover = !!hoveredLiftId && id === hoveredLiftId;
        let isActive = !!activeLiftId && id === activeLiftId;
        $(this)
            .toggleClass('content-map-lift-list__row--active', isActive)
            .toggleClass('content-map-lift-list__row--hover', isHover && !isActive)
            .attr('aria-pressed', isActive ? 'true' : 'false');
    });
}

// Below 900px, .content-map-lift-list__map-wrap (the map) goes from sitting
// beside the row list to sticking above it (see content-map-lift-list.css's
// mobile override) — scrollIntoView's own 'nearest' can't know about that
// sticky occluder (or the fixed bottom .console bar), so it can still land a
// row half-hidden behind either one. Below that breakpoint, scroll explicitly
// so the row ends up right below the sticky map instead of wherever
// "nearest" picks.
function scrollRowIntoView(row) {
    let reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let behavior = reduce ? 'auto' : 'smooth';

    if (!matchMedia('(max-width: 900px)').matches) {
        row.scrollIntoView({ behavior, block: 'nearest' });
        return;
    }

    let mapWrap = document.querySelector('.content-map-lift-list__map-wrap');
    let consoleBar = document.querySelector('.console');
    // .content-map-lift-list__map-wrap::after is a decorative fade-out
    // gradient sitting right below the map (see content-map-lift-list.css) —
    // land the row below that too, not just below the map itself, or its top
    // ends up faded out underneath it.
    let fadeHeight = mapWrap ? parseFloat(getComputedStyle(mapWrap, '::after').height) || 0 : 0;
    let topLimit = mapWrap ? mapWrap.getBoundingClientRect().bottom + fadeHeight : 0;
    let bottomLimit = consoleBar ? consoleBar.getBoundingClientRect().top : window.innerHeight;
    let rowRect = row.getBoundingClientRect();

    let delta = rowRect.top - topLimit;
    if (rowRect.height <= bottomLimit - topLimit) {
        window.scrollBy({ top: delta, left: 0, behavior });
    } else {
        // Row taller than the space between map and console — align it to
        // whichever edge is currently more hidden instead of forcing it
        // fully below the map (impossible without hiding its own bottom).
        window.scrollBy({ top: rowRect.bottom > bottomLimit ? rowRect.bottom - bottomLimit : delta, left: 0, behavior });
    }
}

function setActiveLift(liftId) {
    activeLiftId = (activeLiftId === liftId) ? null : liftId;
    applyActiveHighlight();

    if (activeLiftId) {
        let row = document.querySelector('#liftRows .content-map-lift-list__row[data-lift-id="' + activeLiftId + '"]');
        if (row) {
            scrollRowIntoView(row);
        }
    }
}

function setHoveredLift(liftId) {
    hoveredLiftId = liftId;
    applyActiveHighlight();
}

function bindBahns($scope) {
    $scope.find('.bahn').each(function () {
        let id = $(this).attr('data-lift-id');
        let $bahn = $(this);

        // Cache the name-only label the SVG ships with — updateBahnAvailability()
        // rebuilds aria-label from this on every call, so re-running it never
        // appends the status suffix on top of an already-suffixed label.
        $bahn.data('baseLabel', $bahn.attr('aria-label'));

        $bahn.on('click', function () {
            if ($bahn.hasClass('bahn--disabled')) {
                return;
            }
            setActiveLift(id);
        });
        $bahn.on('mouseenter', function () {
            if ($bahn.hasClass('bahn--disabled')) {
                return;
            }
            setHoveredLift(id);
        });
        $bahn.on('mouseleave', function () {
            setHoveredLift(null);
        });
        $bahn.on('keydown', function (e) {
            if ($bahn.hasClass('bahn--disabled')) {
                return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveLift(id);
            }
        });
    });
}

function bindLiftRows($scope) {
    $scope.find('.content-map-lift-list__row').each(function () {
        let id = $(this).attr('data-lift-id');

        $(this).on('click', function () {
            setActiveLift(id);
        });
        $(this).on('mouseenter', function () {
            setHoveredLift(id);
        });
        $(this).on('mouseleave', function () {
            setHoveredLift(null);
        });
    });
}

export function init() {
    onInitModulesInsScope(function initInScope($scope) {
        bindBahns($scope);
        bindLiftRows($scope);
        // Re-evaluated on every call, not just $scope's content — bahns
        // never appear inside $scope on an ajax-form swap (only the fresh
        // rows do), but availability still needs to be recomputed against
        // whatever row list the swap just inserted.
        updateBahnAvailability();
    });
}
